import type { VideoLayout } from "./videoLayout";

export interface User {
  id: string;
  username: string;
  displayName: string;
  permissions: string[];
  defaultPage: string;
  sidebarCollapsed: boolean;
  videoLayout: VideoLayout;
  cameraLayout: Array<string | null>;
}

const errorMessages: Record<string, string> = {
  AUTH_REQUIRED: "로그인이 필요합니다.",
  INVALID_CREDENTIALS: "계정 또는 비밀번호가 올바르지 않습니다.",
  CURRENT_PASSWORD_INVALID: "현재 비밀번호가 올바르지 않습니다.",
  ACCOUNT_LOCKED: "로그인 실패 횟수를 초과해 계정이 잠겼습니다. 잠시 후 다시 시도하거나 관리자에게 잠금 해제를 요청하세요.",
  PERMISSION_DENIED: "이 기능을 사용할 권한이 없습니다.",
  ROLE_ASSIGNMENT_DENIED: "사용자 생성 권한만으로는 관리자 또는 운영자 역할을 지정할 수 없습니다.",
  DUPLICATE_VALUE: "이미 사용 중인 값입니다.",
  CAMERA_PERMISSION_DENIED: "선택한 카메라를 볼 권한이 없습니다.",
  CAMERA_NOT_AVAILABLE: "현재 사용할 수 없는 카메라입니다.",
  CAMERA_HAS_RECORDINGS: "녹화 영상이 남아 있어 카메라만 삭제할 수 없습니다. 녹화 영상도 함께 삭제하거나 저장소에서 먼저 비워주세요.",
  CAMERA_STOP_FAILED: "카메라 녹화를 안전하게 중지하지 못해 삭제를 취소했습니다.",
  CAMERA_RECORDING_PURGE_IN_PROGRESS: "이 카메라의 녹화 영상 삭제가 이미 진행 중입니다.",
  RECORDING_NOT_FOUND: "선택한 시간에는 녹화 영상이 없습니다.",
  RECORDING_LOCKED: "자동 삭제 방지로 잠긴 녹화 파일입니다. 잠금을 해제한 뒤 삭제하세요.",
  RECORDING_NOT_READY: "현재 처리 중인 녹화 파일은 삭제할 수 없습니다.",
  RECORDING_DELETE_FAILED: "녹화 파일을 삭제하지 못했습니다.",
  PLAYBACK_UNAVAILABLE: "녹화 영상을 재생할 수 없습니다.",
  EXPORT_NOT_AVAILABLE: "아직 내려받을 영상이 준비되지 않았습니다.",
  SNAPSHOT_CAPTURE_FAILED: "현재 화면을 저장하지 못했습니다.",
  MOTION_PREVIEW_FAILED: "움직임 제외 영역을 설정할 현재 화면을 불러오지 못했습니다.",
  MOTION_MASK_AREA_TOO_LARGE: "움직임 분석 영역을 화면의 10% 이상 남겨야 합니다.",
  DEFAULT_PAGE_NOT_AVAILABLE: "권한이 없는 화면은 기본 화면으로 지정할 수 없습니다.",
  INVALID_VIDEO_LAYOUT: "카메라 배치가 올바르지 않습니다. 중복 배치와 화면 수를 확인하세요."
};

function cookie(name: string): string {
  return document.cookie.split("; ").find((part) => part.startsWith(`${name}=`))?.split("=").slice(1).join("=") ?? "";
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) headers.set("x-csrf-token", decodeURIComponent(cookie("nvr_csrf")));
  const response = await fetch(path, { ...init, headers, credentials: "same-origin" });
  if (response.status === 401) window.dispatchEvent(new Event("nvr:unauthorized"));
  const data = response.headers.get("content-type")?.includes("application/json") ? await response.json() : null;
  if (!response.ok) {
    const code = data?.error;
    throw new Error(data?.message ?? errorMessages[code] ?? code ?? `요청 처리 실패 (${response.status})`);
  }
  return data as T;
}
